import React, { memo } from 'react';
import { getRatingColor } from '../utils/helpers';
import { getGoalieAssignmentLabel } from '../utils/assignments';
import PlayerNameBadges from './PlayerNameBadges';

const lineupBtnStyle = {
  fontSize: '10px',
  padding: '2px 6px',
  borderRadius: '4px',
  border: '1px solid var(--button-bg)',
  color: 'white',
  cursor: 'pointer',
  minWidth: '68px',
  textAlign: 'center',
  backgroundColor: 'var(--button-bg)',
};

function GoalieTableRow({ goalie, goalieRateColumns, rateMin, rateMax, assignmentMap, onPlayerClick, onAddToLineup, gridTemplate }) {
  const assignment = assignmentMap.get(goalie.id) || '';
  const assigned = Boolean(assignment);

  return (
    <div
      style={{ gridTemplateColumns: gridTemplate }}
      className={`virtual-table-row player-row ${assigned ? 'assigned' : ''}`}
      onClick={(e) => onPlayerClick(e, goalie)}
    >
      <div className="virtual-table-cell sticky-column player-name-cell">
        <div className="player-name-row">
          <span className="player-name-text">{goalie.name}</span>
          <PlayerNameBadges player={goalie} size="large" showPlayerType={false} />
          <button
            className="wishlist-btn"
            onClick={(e) => {
              e.stopPropagation();
              onAddToLineup(goalie, e);
            }}
            style={lineupBtnStyle}
          >
            + Lineup
          </button>
        </div>
      </div>
      <div className="virtual-table-cell">G</div>
      <div className="virtual-table-cell">{goalie.age || '-'}</div>
      <div className="virtual-table-cell">{goalie.team === 'N/A' ? 'FA' : goalie.team}</div>
      <div className="virtual-table-cell">
        ${goalie.salary ? Number(goalie.salary).toLocaleString('en-US') : '0'}
      </div>
      <div className="virtual-table-cell">
        {assigned ? (
          <div className="assigned-badge" title={getGoalieAssignmentLabel(assignment)}>
            G{assignment ? assignment.replace('goalie', '') : '1'}
          </div>
        ) : (
          <div className="unassigned-badge" title="Click to assign">
            •
          </div>
        )}
      </div>
      {goalieRateColumns.map((col) => {
        const value = goalie[col.name] || 0;
        const backgroundColor = getRatingColor(value, rateMin, rateMax);
        return (
          <div key={col.name} className="virtual-table-cell rating-cell" style={{ backgroundColor }}>
            {value}
          </div>
        );
      })}
    </div>
  );
}

const MemoizedGoalieTableRow = memo(GoalieTableRow);

function VirtualizedGoalieTable({
  goalies,
  goalieRateColumns,
  rateMin,
  rateMax,
  sortColumn,
  sortDirection,
  onHeaderClick,
  assignmentMap,
  onPlayerClick,
  onAddToLineup,
}) {
  const gridTemplate = `180px 40px 50px 90px 90px 70px repeat(${goalieRateColumns.length}, 44px)`;

  return (
    <div className="players-table-container virtual-players-table">
      <div className="virtual-table-header" style={{ gridTemplateColumns: gridTemplate }}>
        <div className="virtual-table-cell sticky-column sortable-header" onClick={() => onHeaderClick('name')}>
          Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell">Pos</div>
        <div className="virtual-table-cell sortable-header" onClick={() => onHeaderClick('age')}>
          Age {sortColumn === 'age' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell sortable-header" onClick={() => onHeaderClick('team')}>
          Team {sortColumn === 'team' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell sortable-header" onClick={() => onHeaderClick('salary')}>
          Salary {sortColumn === 'salary' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell">Status</div>
        {goalieRateColumns.map((col) => (
          <div
            key={col.name}
            className="virtual-table-cell rating-header sortable-header"
            onClick={() => onHeaderClick(col.name)}
          >
            {col.name} {sortColumn === col.name && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
        ))}
      </div>
      <div className="virtual-table-body">
        {goalies.map((goalie) => (
          <MemoizedGoalieTableRow
            key={goalie.id}
            goalie={goalie}
            goalieRateColumns={goalieRateColumns}
            rateMin={rateMin}
            rateMax={rateMax}
            assignmentMap={assignmentMap}
            onPlayerClick={onPlayerClick}
            onAddToLineup={onAddToLineup}
            gridTemplate={gridTemplate}
          />
        ))}
      </div>
    </div>
  );
}

export default memo(VirtualizedGoalieTable);
